import React, { useState, useEffect } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { AiOutlineClose, AiOutlineMenu } from 'react-icons/ai';
import Logo from '../../assets/logo.png' 
import { useContentful } from '../contentful-context';
import { getTestBucket, checkUrlOverride } from '../../utils/abtest';

function NavLink({ to, children, onClick }) {
  const location = useLocation();
  const isActive = location.pathname === to || 
    (to !== '/' && location.pathname.startsWith(to));
  
  return (
    <Link to={to} onClick={onClick}>
      <li className={`nav-link font-medium transition-colors duration-300 ${
        isActive ? 'text-construx-red' : 'text-warm-800 hover:text-construx-red'
      }`}>
        {children}
      </li>
    </Link>
  );
}

function Navbar() {
  const content = useContentful();
  const location = useLocation();
  const [nav, setNav] = useState(true);
  const [showStoryLink, setShowStoryLink] = useState(true);

  // Check homepage experiment bucket to determine if "Our Story" should be hidden
  useEffect(() => {
    // Check URL override first (for debug panel forcing)
    const urlOverride = checkUrlOverride('homepage-test');
    const bucket = urlOverride || getTestBucket('homepage-test', 0.5).bucket;
    
    if (process.env.REACT_APP_ENABLE_LOGGING === 'true') {
      console.log('[Navbar] Homepage bucket:', bucket);
      console.log('[Navbar] Show Story link:', bucket !== 'B');
    }
    
    // Hide "Our Story" if user is in Bucket B (System variant)
    const shouldShow = bucket !== 'B';
    setShowStoryLink(shouldShow);
  }, []);

  const handleNav = () => {
    setNav(!nav);
  }

  const closeNav = () => {
    setNav(true);
  }

  const handleLogoClick = () => {
    if (location.pathname === '/product') {
      window.scrollTo(0, 0);
    }
    closeNav();
  }

  if(!content.images.length){
    return null;
  }

  const { image } = content.images.find(image => image.id == "Construx-Black-FullColor-Tag");
  const { file } = image.fields;

  return (
    <>
      <nav className='sticky top-0 z-50 glass-nav text-warm-800 flex items-center h-20 w-full mx-auto px-6 justify-between transition-all duration-300'>
        <Link to='/product' onClick={handleLogoClick} className='transition-transform duration-300 hover:scale-105'>
          <img src={file.url} alt='construx logo' className='w-32 md:w-52' />
        </Link>
        
        {/* Desktop Navigation */}
        <ul className='hidden md:flex items-center gap-1'>
          {showStoryLink && <NavLink to='/story'>Our Story</NavLink>}
          <NavLink to='/product'>Product</NavLink>
          <NavLink to='/models'>Models</NavLink>
          <NavLink to='/partners'>Partners</NavLink>
          <NavLink to='/blog'>Blog</NavLink>
          <NavLink to='/faqs'>FAQ</NavLink>
          <NavLink to='/interest'>Contact</NavLink>
        </ul>
        
        {/* Mobile Menu Button */}
        <button 
          onClick={handleNav} 
          className='block md:hidden p-2 rounded-lg hover:bg-gray-100 transition-colors'
          aria-label='Toggle menu'
        >
          {!nav ? 
            <AiOutlineClose size={24} /> :
            <AiOutlineMenu size={24} />
          }
        </button>
        
        {/* Mobile Menu Overlay */}
        <div 
          className={`fixed inset-0 bg-black/50 z-40 transition-opacity duration-300 md:hidden ${
            !nav ? 'opacity-100' : 'opacity-0 pointer-events-none'
          }`}
          onClick={closeNav}
        />
        
        {/* Mobile Menu Panel */}
        <div className={`fixed left-0 top-0 w-[75%] max-w-[300px] bg-white h-screen ease-out duration-300 z-50 shadow-2xl ${
          !nav ? 'translate-x-0' : '-translate-x-full'
        }`}>
          <div className='p-6 border-b border-gray-100 bg-white'>
            <Link to='/product' onClick={handleLogoClick}>
              <img src={file.url} alt='construx logo' className='w-40' />
            </Link>
          </div>
          <ul className='py-4 bg-white'>
            <Link to='/' onClick={closeNav}>
              <li className='px-6 py-4 font-medium text-gray-800 bg-white hover:bg-gray-100 transition-colors'>Home</li>
            </Link>
            {showStoryLink && (
              <Link to='/story' onClick={closeNav}>
                <li className='px-6 py-4 font-medium text-gray-800 bg-white hover:bg-gray-100 transition-colors'>Our Story</li>
              </Link>
            )}
            <Link to='/product' onClick={closeNav}>
              <li className='px-6 py-4 font-medium text-gray-800 bg-white hover:bg-gray-100 transition-colors'>Product</li>
            </Link>
            <Link to='/models' onClick={closeNav}>
              <li className='px-6 py-4 font-medium text-gray-800 bg-white hover:bg-gray-100 transition-colors'>Models</li>
            </Link>
            <Link to='/partners' onClick={closeNav}>
              <li className='px-6 py-4 font-medium text-gray-800 bg-white hover:bg-gray-100 transition-colors'>Partners</li>
            </Link>
            <Link to='/blog' onClick={closeNav}>
              <li className='px-6 py-4 font-medium text-gray-800 bg-white hover:bg-gray-100 transition-colors'>Blog</li>
            </Link>
            <Link to='/faqs' onClick={closeNav}>
              <li className='px-6 py-4 font-medium text-gray-800 bg-white hover:bg-gray-100 transition-colors'>FAQ</li>
            </Link>
            <Link to='/interest' onClick={closeNav}>
              <li className='px-6 py-4 font-medium text-gray-800 bg-white hover:bg-gray-100 transition-colors'>Contact</li>
            </Link>
          </ul>
        </div>
      </nav>
    </>
  )
}

export default Navbar
